export type SystemRole =
  | 'system_admin'
  | 'system_engineering_manager'
  | 'system_engineer'
  | 'system_support'
  | 'user';

const ENGINEER_PRIVILEGE_ROLES = new Set<string>([
  'system_admin',
  'system_engineer',
  'system_engineering_manager',
]);

const SUPPORT_PRIVILEGE_ROLES = new Set<string>([
  'system_admin',
  'system_support',
  'system_engineering_manager',
]);

const SUPPORT_STAFF_ROLES = new Set<string>([
  'system_admin',
  'system_engineering_manager',
  'system_engineer',
  'system_support',
]);

const ROLE_LABELS: Record<string, string> = {
  system_admin: 'System Admin',
  system_engineering_manager: 'Engineering Manager',
  system_engineer: 'System Engineer',
  system_support: 'System Support',
  user: 'User',
};

export const isSystemAdmin = (role?: string | null): boolean => role === 'system_admin';

export const hasEngineerPrivileges = (
  role?: string | null,
): boolean => Boolean(role && ENGINEER_PRIVILEGE_ROLES.has(role));

export const hasSupportPrivileges = (
  role?: string | null,
): boolean => Boolean(role && SUPPORT_PRIVILEGE_ROLES.has(role));

export const isSupportStaff = (role?: string | null): boolean => Boolean(role && SUPPORT_STAFF_ROLES.has(role));

export const receivesGlobalSupportAlerts = (role?: string | null): boolean => hasSupportPrivileges(role) || role === 'support';

export const getSystemRoleLabel = (role?: string | null): string => (role && ROLE_LABELS[role]) || 'User';

/** Additional roles an admin can grant on top of the system role (Manage Users → Roles). */
export const ADDITIONAL_ROLE_GROUPS: Array<{ label: string; roles: Array<{ value: string; label: string }> }> = [
  { label: 'Development', roles: [{ value: 'senior_dev', label: 'Senior Developer' }, { value: 'lead_dev', label: 'Lead Developer' }] },
  {
    label: 'Marketing',
    roles: [
      { value: 'marketing', label: 'Marketing (all marketing pages)' },
      { value: 'seo_backlinks', label: 'SEO Backlinks' },
      { value: 'marketing_bidding', label: 'Marketing Bidding' },
      { value: 'social_media', label: 'Social Media' },
      { value: 'marketing_campaigns', label: 'Online Campaigns' },
      { value: 'offline_marketing', label: 'Offline Marketing' },
    ],
  },
  {
    label: 'Operations',
    roles: [
      { value: 'operations', label: 'Operations (all operations pages)' },
      { value: 'meeting_records', label: 'Meeting Records' },
    ],
  },
  {
    label: 'Sales',
    roles: [
      { value: 'sales', label: 'Sales (all sales pages)' },
      { value: 'lead_management', label: 'Lead Management' },
    ],
  },
];

export type TeamSection =
  | 'backlinks' | 'bids' | 'social' | 'campaigns' | 'letters' | 'meetings' | 'leads' | 'email' | 'documents';

/** section -> roles that open it; mirrors mostlyagent/api/middleware/record_access.py. */
const TEAM_SECTION_ROLES: Record<TeamSection, string[]> = {
  backlinks: ['marketing', 'seo_backlinks'],
  bids: ['marketing', 'marketing_bidding'],
  social: ['marketing', 'social_media'],
  campaigns: ['marketing', 'marketing_campaigns'],
  letters: ['marketing', 'offline_marketing'],
  meetings: ['operations', 'meeting_records'],
  leads: ['sales', 'lead_management'],
  email: ['marketing', 'marketing_campaigns'],
  documents: ['marketing', 'marketing_campaigns', 'marketing_bidding', 'sales', 'lead_management'],
};

export const TEAM_SECTIONS = Object.keys(TEAM_SECTION_ROLES) as TeamSection[];

export const canAccessTeamSection = (
  user: { system_role?: string | null; additional_roles?: string[] } | null | undefined,
  section: TeamSection,
): boolean => {
  if (!user) return false;
  if (isSystemAdmin(user.system_role)) return true;
  const roles = user.additional_roles ?? [];

  return TEAM_SECTION_ROLES[section].some((role) => roles.includes(role));
};
